// Boost Blenz Admin — Support views: Help Desk (AI + human) and Knowledge Base (CRUD + guardrails).

import React from "react";
import { FIGTREE, OSWALD, useT, useIsMobile } from "../theme";
import { Icon, Title, Card, Badge, Btn, SectionTitle, Toggle } from "../atoms";
import { BB_HELP } from "../data/help";
import { InTheTracker } from "./app/customers";

// ============================================================ HELP DESK
// Live Gmail inbox (support@ + boostblenz@gmail merged; dustin@ personal) — list
// customer threads, read full bodies, tag/triage (mark read/unread, star, archive,
// to-do tag), search across bodies, view image attachments, and reply. Redesigned
// 2026-08 to the reviewed mockup: independent-scroll columns on desktop, a slide-up
// reading sheet on mobile, and a durable customer-service tag system.

// Pull a display name out of an RFC5322 From header: "Jane Doe <j@x.com>".
function parseSender(from: string): { name: string; email: string } {
  const m = /^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/.exec(from || "");
  if (m) return { name: (m[1] || "").trim() || m[2].trim(), email: m[2].trim() };
  return { name: (from || "").trim(), email: (from || "").trim() };
}

// Timestamp broken into a relative word + the real date + the time, so the reader
// always sees BOTH "Today"/"Yesterday"/weekday AND the actual date to verify the day.
function fmtParts(iso: string): { rel: string; date: string; time: string } {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { rel: "", date: "", time: "" };
  const now = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(d)) / 86400000);
  const rel = days === 0 ? "Today" : days === 1 ? "Yesterday" : (days > 1 && days < 7) ? d.toLocaleDateString("en-US", { weekday: "long" }) : "";
  return {
    rel,
    date: d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
    time: d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
  };
}
// Compact stamp for the narrow list column: time if today, else weekday this week, else "Mon D".
function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = fmtParts(iso);
  if (p.rel === "Today") return p.time;
  if (p.rel === "Yesterday") return "Yesterday";
  if (p.rel) return p.rel.slice(0, 3);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
// Surface an order number if one appears in the subject/snippet — a nice-to-have on the header.
// Order names are #BB_18033 (numbered from BB_1002), and customers write them every which way:
// "BB-18033", "bb18033", "#18033", "order 18033". All count, shown in the store's own format.
// A bare "18033" only counts as the whole subject — in running text a lone five-digit number is
// as often a ZIP or a street number, and a wrong order on the header is worse than none.
const ORDER_NO_RES = [
  /(?:^|[^a-z0-9])#?\s?bb\s?[_-]?\s?(\d{4,6})(?![\w%]|[.,]\d)/i, // #BB_18033 · BB-18033 · bb18033 · bb 18033
  /\border\s*(?:number|num|no\.?|id|is)?\s*[:#]?\s*(\d{4,6})(?![\w%]|[.,]\d)/i, // order 18033 · order #18033 · order no. 18033
  /(?:^|[^\w&])#\s?(\d{4,6})(?![\w%]|[.,]\d)/, // #18033 — not "&#8217;", the HTML entities Gmail leaves in snippets
];
function orderNo(subject: string, snippet = ""): string {
  const text = subject + "\n" + snippet;
  for (const re of ORDER_NO_RES) {
    const m = re.exec(text);
    if (m) return "#BB_" + m[1];
  }
  const bare = /^\s*(?:(?:re|fwd?)\s*:\s*)*(\d{5})\s*[.!?]*\s*$/i.exec(subject);
  return bare ? "#BB_" + bare[1] : "";
}
// How a relayed customer message reached us (see RELAYS in worker/gmailwatch.ts).
const VIA_LABEL: Record<string, string> = { tracker: "the Detox Tracker app" };
function prettySize(n: number): string {
  if (!n) return "";
  return n < 1024 * 1024 ? Math.max(1, Math.round(n / 1024)) + " KB" : (n / 1048576).toFixed(1) + " MB";
}

// The customer-service to-do tags — durable, team-shared buckets. Colors match the mockup.
const TAGS = [
  { k: "refund", label: "Refund", color: "#ff8b7d" },
  { k: "replacement", label: "Replacement", color: "#ffce5a" },
  { k: "followup", label: "Follow-up", color: "#b9a9ff" },
  { k: "address", label: "Address issue", color: "#f8925c" },
] as const;
const TAGMAP: Record<string, { k: string; label: string; color: string }> = Object.fromEntries(TAGS.map((t) => [t.k, t]));

// Turn bare URLs in a plain-text body into clickable links (for text-only emails).
function linkify(text: string, color: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /(https?:\/\/[^\s<>()]+)/g;
  let last = 0, m: RegExpExecArray | null, i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<a key={i++} href={m[1]} target="_blank" rel="noopener noreferrer" style={{ color, wordBreak: "break-all" }}>{m[1]}</a>);
    last = m.index + m[1].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// One email body, rendered like a real mail client. We PREFER the rich HTML part so
// links, buttons, and images show — falling back to (linkified) plain text only when
// there's no HTML. HTML renders in an iframe sandboxed WITHOUT allow-scripts, so no
// script in a customer email can ever run; `allow-same-origin` (safe with scripts
// disabled) lets us auto-size the frame to the message's full height so nothing is
// cut off, and `allow-popups` lets links open in a new tab. The frame grows to fit —
// the page scrolls, never the message.
function EmailBody({ text, html }: { text: string; html: string }) {
  const t = useT();
  const ref = React.useRef<HTMLIFrameElement>(null);
  const [h, setH] = React.useState(160);
  const hasHtml = !!(html && html.trim());

  React.useEffect(() => {
    if (!hasHtml) return;
    const ifr = ref.current;
    if (!ifr) return;
    const measure = () => {
      try {
        const d = ifr.contentDocument;
        const body = d?.body;
        if (body) {
          const nh = Math.max(body.scrollHeight, d!.documentElement.scrollHeight);
          if (nh > 0) setH((cur) => (Math.abs(nh - cur) > 2 ? nh + 6 : cur));
        }
      } catch { /* opaque origin — keep default height */ }
    };
    const onLoad = () => { measure(); setTimeout(measure, 200); setTimeout(measure, 700); setTimeout(measure, 1600); };
    ifr.addEventListener("load", onLoad);
    onLoad(); // in case it loaded before the listener attached
    let ro: any;
    try {
      const bodyEl = ifr.contentDocument?.body;
      if (bodyEl && (window as any).ResizeObserver) { ro = new ResizeObserver(measure); ro.observe(bodyEl); }
    } catch { /* ignore */ }
    return () => { ifr.removeEventListener("load", onLoad); if (ro) ro.disconnect(); };
  }, [hasHtml, html]);

  if (hasHtml) {
    const doc = `<!doctype html><html><head><base target="_blank"><meta name="color-scheme" content="light">` +
      `<style>html,body{margin:0;padding:0;background:#fff}` +
      `body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;font-size:14px;line-height:1.5;color:#1a1a1a;padding:6px 4px;word-wrap:break-word;overflow-wrap:break-word}` +
      `img{max-width:100%;height:auto}a{color:#0a58ca}table{max-width:100%}` +
      `*{max-width:100%}</style></head><body>${html}</body></html>`;
    return (
      <iframe
        ref={ref}
        title="email"
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        srcDoc={doc}
        style={{ width: "100%", height: h, border: "none", borderRadius: 8, background: "#fff", display: "block", colorScheme: "light" }}
      />
    );
  }
  if (text && text.trim()) {
    return <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.fg2, lineHeight: 1.62, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{linkify(text, t.purple)}</div>;
  }
  return <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.faint, fontStyle: "italic" }}>(no readable content)</div>;
}

// An attachment chip. Nothing downloads until the owner clicks: images then load
// inline; other files open in a new tab (served as a forced download by the worker).
function AttachmentChip({ account, messageId, att }: any) {
  const t = useT();
  const [open, setOpen] = React.useState(false);
  const url = `/api/helpdesk/attachment?account=${encodeURIComponent(account)}&message=${encodeURIComponent(messageId)}&id=${encodeURIComponent(att.attachmentId)}&mime=${encodeURIComponent(att.mimeType)}&name=${encodeURIComponent(att.filename)}`;
  return (
    <div>
      <button
        onClick={() => (att.isImage ? setOpen((v: boolean) => !v) : window.open(url, "_blank"))}
        style={{ display: "flex", alignItems: "center", gap: 10, background: t.surface2, border: "1px solid " + t.border, borderRadius: 12, padding: "8px 12px", cursor: "pointer", maxWidth: "100%" }}
      >
        <span style={{ width: 32, height: 32, borderRadius: 8, background: t.purpleSoft, display: "grid", placeItems: "center", flexShrink: 0 }}>
          <Icon name="image" size={16} color={t.purple} />
        </span>
        <span style={{ minWidth: 0, textAlign: "left" }}>
          <span style={{ display: "block", fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5, color: t.fg, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{att.filename}</span>
          <span style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint }}>{[prettySize(att.size), att.isImage ? "image" : "file"].filter(Boolean).join(" · ")}</span>
        </span>
        <span style={{ fontFamily: FIGTREE, fontSize: 11, fontWeight: 800, color: t.accent, flexShrink: 0 }}>{att.isImage ? (open ? "Hide" : "Click to view") : "Download"}</span>
      </button>
      {open && att.isImage ? (
        <div style={{ marginTop: 8 }}>
          <img src={url} alt={att.filename} style={{ maxWidth: "min(420px,100%)", borderRadius: 12, border: "1px solid " + t.border, display: "block" }} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * `deepLink` / `go` (App section v2): a thread has a URL — #/support/<account>/<threadId> — so a
 * customer profile can open it and a link pasted into chat lands on it. Opening a thread writes
 * the URL; arriving on a URL opens the thread. The inbox itself is unchanged.
 */
export function HelpDeskView({ deepLink, go }: { deepLink?: { account: string; threadId: string } | null; go?: (hash: string) => void } = {}) {
  const t = useT();
  const isMobile = useIsMobile(840);
  const isWide = !useIsMobile(1280);
  const [threads, setThreads] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [sel, setSel] = React.useState<string | null>(null);
  const [selAccount, setSelAccount] = React.useState<string>("");
  const [thread, setThread] = React.useState<any>(null);
  const [threadLoading, setThreadLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [tab, setTab] = React.useState<"active" | "drafts" | "answered" | "archived">("active");
  const [view, setView] = React.useState<"customer" | "personal">("customer");
  const [archived, setArchived] = React.useState<any[]>([]);
  const [archLoaded, setArchLoaded] = React.useState(false);
  const [draftList, setDraftList] = React.useState<any[]>([]); // full draft set incl. archived-thread drafts
  const [meEmail, setMeEmail] = React.useState("");
  const [meRole, setMeRole] = React.useState("");
  const [replyText, setReplyText] = React.useState("");
  const [sending, setSending] = React.useState(false);
  // New: search, tag filter, tag menu, and the mobile reading sheet.
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState<any[]>([]);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [tagFilter, setTagFilter] = React.useState<string>("all");
  const [tagMenu, setTagMenu] = React.useState(false);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [attach, setAttach] = React.useState<any[]>([]); // outgoing attachments for the open reply
  const [showBlocked, setShowBlocked] = React.useState(false);

  const loadList = React.useCallback((fresh: boolean, silent = false) => {
    if (!silent) setLoading(true);
    fetch(`/api/helpdesk/threads?view=${view}${fresh ? "&fresh=1" : ""}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (Array.isArray(d?.threads)) setThreads(d.threads); if (!silent) setLoading(false); })
      .catch(() => { setErr("Couldn't reach the inbox."); if (!silent) setLoading(false); });
  }, [view]);
  React.useEffect(() => { loadList(false); }, [loadList]);

  const loadArchived = React.useCallback((fresh: boolean, silent = false) => {
    if (!silent) setLoading(true);
    fetch(`/api/helpdesk/archived?view=${view}${fresh ? "&fresh=1" : ""}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (Array.isArray(d?.threads)) setArchived(d.threads); setArchLoaded(true); if (!silent) setLoading(false); })
      .catch(() => { if (!silent) setLoading(false); });
  }, [view]);

  // All threads with a saved draft (spans inbox + archive). Loaded on mount so the
  // sidebar/Drafts count is right, and refreshed on the poll.
  const loadDrafts = React.useCallback(() => {
    fetch(`/api/helpdesk/drafts?view=${view}`).then((r) => (r.ok ? r.json() : null)).then((d) => { if (Array.isArray(d?.threads)) setDraftList(d.threads); }).catch(() => {});
  }, [view]);
  React.useEffect(() => { loadDrafts(); }, [loadDrafts]);

  React.useEffect(() => { setSel(null); setThread(null); setSelAccount(""); setReplyText(""); setArchLoaded(false); setDraftList([]); setTab("active"); setQuery(""); setResults([]); setTagFilter("all"); setSheetOpen(false); }, [view]);

  React.useEffect(() => {
    fetch("/api/me").then((r) => (r.ok ? r.json() : null)).then((d) => { setMeEmail(String(d?.email || "").toLowerCase()); setMeRole(String(d?.role || "")); }).catch(() => {});
  }, []);

  React.useEffect(() => {
    const iv = setInterval(() => { if (!document.hidden) { loadList(true, true); loadDrafts(); if (tab === "archived") loadArchived(true, true); } }, 60000);
    return () => clearInterval(iv);
  }, [loadList, loadArchived, loadDrafts, tab]);

  React.useEffect(() => { if (tab === "archived" && !archLoaded) loadArchived(false); }, [tab, archLoaded, loadArchived]);

  // Server-side search across From / Subject / body (Gmail `q`), debounced.
  const searching = query.trim().length >= 2;
  React.useEffect(() => {
    if (!searching) { setResults([]); setSearchLoading(false); return; }
    let live = true;
    setSearchLoading(true);
    const id = setTimeout(() => {
      fetch(`/api/helpdesk/search?view=${view}&q=${encodeURIComponent(query.trim())}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (live) { setResults(Array.isArray(d?.threads) ? d.threads : []); setSearchLoading(false); } })
        .catch(() => { if (live) setSearchLoading(false); });
    }, 320);
    return () => { live = false; clearTimeout(id); };
  }, [query, view, searching]);

  const openThread = React.useCallback((id: string, account: string) => {
    if (go) go(`#/support/${account}/${id}`);
    setSel(id); setSelAccount(account); setThread(null); setThreadLoading(true); setReplyText(""); setTagMenu(false);
    if (isMobile) setSheetOpen(true);
    fetch(`/api/helpdesk/thread/${encodeURIComponent(id)}?account=${encodeURIComponent(account)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        setThread(d && !d.error ? d : null);
        setThreadLoading(false);
        if (d && !d.error) {
          setThreads((ts) => ts.map((x) => (x.threadId === id && x.account === account ? { ...x, unread: false } : x)));
          setResults((rs) => rs.map((x) => (x.threadId === id && x.account === account ? { ...x, unread: false } : x)));
        }
      })
      .catch(() => setThreadLoading(false));
  }, [isMobile, go]);

  // Arriving on #/support/<account>/<threadId> (from a profile, or a pasted link) opens it.
  React.useEffect(() => {
    if (!deepLink?.threadId) return;
    if (deepLink.threadId === sel && deepLink.account === selAccount) return;
    const acc = ["support", "dustin", "reporting"].includes(deepLink.account) ? deepLink.account : "support";
    openThread(deepLink.threadId, acc);
  }, [deepLink?.threadId, deepLink?.account]);

  async function triage(action: string, threadId: string, account: string) {
    if (action === "archive" && !window.confirm("Archive this conversation? It leaves the inbox and moves to the Archived tab (and Gmail's All Mail). Nothing is deleted, and a customer reply brings it back to Needs response automatically.")) return;
    setBusy(true);
    const r = await fetch("/api/helpdesk/triage", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ threadId, action, account }),
    }).then((x) => (x.ok ? x.json() : null)).catch(() => null);
    setBusy(false);
    if (r?.threads) setThreads(r.threads);
    if (action === "archive" || action === "unarchive") { setSel(null); setThread(null); setSheetOpen(false); loadArchived(true, true); }
    else if (action === "unread") {
      setThreads((ts) => ts.map((x) => (x.threadId === threadId && x.account === account ? { ...x, unread: true } : x)));
      setResults((rs) => rs.map((x) => (x.threadId === threadId && x.account === account ? { ...x, unread: true } : x)));
    }
  }

  async function claim(threadId: string, release: boolean, account: string) {
    const r = await fetch("/api/helpdesk/claim", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ threadId, release, account }),
    }).then((x) => (x.ok ? x.json() : null)).catch(() => null);
    if (!r) return;
    const set = (arr: any[]) => arr.map((x) => (x.threadId === threadId && x.account === account ? { ...x, claim: release ? null : r.claim } : x));
    setThreads(set); setResults(set); setArchived(set);
  }

  // Set (or clear) a thread's to-do tag. Updates every list it might live in.
  async function setThreadTag(threadId: string, account: string, tag: string) {
    setTagMenu(false);
    const r = await fetch("/api/helpdesk/tag", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ threadId, account, tag }),
    }).then((x) => (x.ok ? x.json() : null)).catch(() => null);
    const next = r && r.ok ? r.tag : tag || null;
    const set = (arr: any[]) => arr.map((x) => (x.threadId === threadId && x.account === account ? { ...x, tag: next } : x));
    setThreads(set); setResults(set); setArchived(set);
  }

  async function sendReplyNow() {
    if (!thread || !sel || (!replyText.trim() && !attach.length) || sending) return;
    const msgs = (thread.messages || []) as any[];
    const lastCust = [...msgs].reverse().find((m) => !m.fromMe) || msgs[msgs.length - 1];
    const to = lastCust?.from || "";
    setSending(true);
    const r = await fetch("/api/helpdesk/reply", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ account: selAccount, threadId: sel, to, subject: thread.subject || "", text: replyText, inReplyTo: lastCust?.messageId || "", references: lastCust?.messageId || "", attachments: attach.map((a) => ({ filename: a.filename, mimeType: a.mimeType, dataB64: a.dataB64 })) }),
    }).then((x) => x.json().catch(() => null)).catch(() => null);
    setSending(false);
    if (r?.ok) { setReplyText(""); lastSaved.current = ""; setAttach([]); if (r.threads) setThreads(r.threads); openThread(sel, selAccount); } // send clears the draft (server-side too)
    else window.alert("Couldn't send the reply: " + (r?.detail || r?.error || "unknown error"));
  }

  // One-click Gmail setup (owner) — create labels, re-apply tags, add noise filters.
  async function setupGmail() {
    if (!window.confirm("Set up Gmail natively?\n\nThis creates the customer-service labels (Refund / Replacement / Follow-up / Address issue), re-applies the tags you've already set to those threads, and adds the inbox-noise filters — replacing an older copy of one of them if it's there. Detox Tracker app questions that the older filter archived go back to the inbox. No email is deleted. Runs on support@boostblenz.com.")) return;
    setBusy(true);
    const r = await fetch("/api/helpdesk/gmail-setup", { method: "POST" }).then((x) => (x.ok ? x.json() : null)).catch(() => null);
    setBusy(false);
    if (!r?.ok) { window.alert("Couldn't set up Gmail: " + (r?.error || "unknown error")); return; }
    let msg = `Gmail setup done on ${r.account}@:\n• Labels ready: ${(r.labels || []).join(", ")}\n• Threads re-tagged: ${r.tagged}\n• Noise filters created: ${r.filtersCreated}\n• Older filters replaced: ${r.filtersReplaced || 0}\n• App questions moved back to the inbox: ${r.rescued || 0}`;
    if (r.filterError) msg += `\n\nA filter couldn't be created: ${r.filterError}`;
    if (r.rescued) loadList(true, true);
    if (r.filtersNeedScope && r.xml) {
      msg += `\n\nFilters need one more Google permission that isn't granted yet. I've downloaded a filter file — import it once in Gmail:\nSettings → See all settings → Filters and Blocked Addresses → Import filters.`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.xml], { type: "application/xml" }));
      a.download = "boost-blenz-cs-filters.xml"; a.click(); URL.revokeObjectURL(a.href);
    }
    window.alert(msg);
  }

  // Read picked files into base64 attachment records (nothing uploads until Send).
  function addFiles(files: FileList | null) {
    if (!files || !files.length) return;
    Promise.all(Array.from(files).map((f) => new Promise<any>((res) => {
      const rd = new FileReader();
      rd.onload = () => { const s = String(rd.result || ""); res({ filename: f.name, mimeType: f.type || "application/octet-stream", dataB64: s.slice(s.indexOf(",") + 1), size: f.size }); };
      rd.onerror = () => res(null);
      rd.readAsDataURL(f);
    }))).then((list) => {
      const good = list.filter(Boolean);
      setAttach((cur) => {
        const next = [...cur, ...good];
        const total = next.reduce((n, x) => n + (x.size || 0), 0);
        if (total > 15 * 1024 * 1024) window.alert("Heads up: attachments total over 15MB. Gmail's limit is ~25MB, and large files (ebooks, the tracker) send more reliably as a link — Google Drive or Dropbox.");
        return next;
      });
    });
  }
  const removeAttach = (i: number) => setAttach((a) => a.filter((_, idx) => idx !== i));

  // "Not a customer" — block the sender and archive the thread so it (and future mail
  // from them) never clutters the Help Desk again.
  async function notCustomer() {
    if (!sel || !selMeta) return;
    const who = parseSender(selMeta.sender || "");
    if (!window.confirm(`Mark ${who.email || who.name} as “not a customer”?\n\nThis archives this conversation and hides all future email from that sender across the whole Help Desk. Nothing is deleted, and you can unblock them later from “Blocked senders.”`)) return;
    setBusy(true);
    const r = await fetch("/api/helpdesk/block", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ account: selAccount, threadId: sel, sender: selMeta.sender }) }).then((x) => (x.ok ? x.json() : null)).catch(() => null);
    setBusy(false);
    if (r?.threads) setThreads(r.threads);
    setSel(null); setThread(null); setSheetOpen(false);
  }

  // ---- Drafts: save an unfinished reply so you can leave and come back to it. ----
  const lastSaved = React.useRef<string>("");
  const saveDraft = React.useCallback((threadId: string, account: string, text: string) => {
    fetch("/api/helpdesk/draft", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ threadId, account, text }) }).catch(() => {});
    const val = text.trim() ? text : null;
    const set = (arr: any[]) => arr.map((x) => (x.threadId === threadId && x.account === account ? { ...x, draft: val } : x));
    setThreads(set); setResults(set); setArchived(set); setDraftList(set);
  }, []);

  // Prefill the composer with this thread's saved draft when it opens. Deps are only
  // sel/selAccount so a background list refresh never clobbers what you're typing.
  React.useEffect(() => {
    if (!sel) { lastSaved.current = ""; return; }
    const meta = [...threads, ...archived, ...results, ...draftList].find((x) => x.threadId === sel && x.account === selAccount);
    const d = meta?.draft || "";
    setReplyText(d);
    lastSaved.current = d;
    setAttach([]); // attachments are per-open-reply, not part of the saved draft
  }, [sel, selAccount]);

  // Auto-save the draft ~0.8s after you stop typing (and on blur, below).
  React.useEffect(() => {
    if (!sel) return;
    const id = setTimeout(() => {
      if (replyText !== lastSaved.current) { saveDraft(sel, selAccount, replyText); lastSaved.current = replyText; }
    }, 800);
    return () => clearTimeout(id);
  }, [replyText, sel, selAccount, saveDraft]);

  function flushDraft() {
    if (sel && replyText !== lastSaved.current) { saveDraft(sel, selAccount, replyText); lastSaved.current = replyText; }
  }
  function discardDraft() {
    if (!sel) return;
    if (!window.confirm("Discard this draft? This only deletes the unsent text you've written — the email stays right where it is in your inbox.")) return;
    setReplyText("");
    lastSaved.current = "";
    saveDraft(sel, selAccount, "");
  }

  const needs = threads.filter((x) => !x.answered);
  const answered = threads.filter((x) => x.answered);
  // Drafts tab = every thread with a saved draft, unioned across every list we hold
  // (inbox, archive, search, dedicated draft list) so an archived-thread draft and a
  // just-typed one both appear. Dedup by mailbox+thread; freshest (inbox) wins.
  const dkey = (x: any) => x.account + ":" + x.threadId;
  const draftMap = new Map<string, any>();
  for (const x of [...draftList, ...archived, ...results, ...threads]) if (x.draft) draftMap.set(dkey(x), x);
  const draftThreads = [...draftMap.values()].sort((a, b) => String(b.receivedAt).localeCompare(String(a.receivedAt)));
  const base = searching ? results : tab === "active" ? needs : tab === "drafts" ? draftThreads : tab === "answered" ? answered : archived;
  const tagCounts: Record<string, number> = { all: base.length };
  for (const tg of TAGS) tagCounts[tg.k] = base.filter((x) => x.tag === tg.k).length;
  const shown = tagFilter === "all" ? base : base.filter((x) => x.tag === tagFilter);
  const selMeta = [...threads, ...archived, ...results, ...draftList].find((x) => x.threadId === sel && x.account === selAccount);
  const viewingArchived = tab === "archived" && !searching;
  const canSend = !!selMeta && selMeta.account !== "reporting";
  const showPersonal = meRole === "owner";

  const TabBtn = ({ id, label, count, tone }: any) => {
    const on = tab === id && !searching;
    return (
      <button onClick={() => { setTab(id); setQuery(""); }} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", border: "none", borderBottom: "2px solid " + (on ? t.accent : "transparent"), background: "transparent", cursor: "pointer", fontFamily: FIGTREE, fontWeight: on ? 800 : 600, fontSize: 13.5, color: on ? t.fg : t.muted }}>
        {label}
        <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 11.5, padding: "1px 8px", borderRadius: 999, background: on ? tone : t.surface2, color: on ? "#2a1608" : t.muted }}>{count == null ? "…" : count}</span>
      </button>
    );
  };

  // ---- reading pane, shared by the desktop column and the mobile slide-up sheet ----
  // NB: this is a plain function returning JSX, invoked inline as `renderPane(...)` —
  // NOT a nested <Component/>. Rendering it as a component gives it a fresh identity
  // every render, which would remount the subtree and kick focus out of the reply box
  // on every keystroke. Inlining its output keeps the textarea mounted and focused.
  const actBtn: React.CSSProperties = { fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 12px", borderRadius: 10, border: "1px solid " + t.borderStrong, background: "transparent", color: t.fg2, cursor: "pointer" };
  const curTag = selMeta?.tag ? TAGMAP[selMeta.tag] : null;

  function renderPane({ sheet }: { sheet: boolean }) {
    if (!sel) return <div style={{ flex: 1, display: "grid", placeItems: "center", padding: 24, fontFamily: FIGTREE, fontSize: 13.5, color: t.muted }}>Select a conversation to read it.</div>;
    const s = parseSender(selMeta?.sender || "");
    const first = thread?.messages?.[0];
    const stampSrc = first ? fmtParts(first.date) : fmtParts(selMeta?.receivedAt || "");
    const ord = orderNo(selMeta?.subject || "", selMeta?.snippet || "");
    return (
      <>
        <div style={{ flex: "none", padding: "15px 18px", borderBottom: "1px solid " + t.border, background: t.surface }}>
          {sheet ? (
            <button onClick={() => setSheetOpen(false)} style={{ ...actBtn, marginBottom: 12, borderRadius: 999, fontWeight: 800 }}><Icon name="chevron" size={14} color={t.fg2} style={{ transform: "rotate(90deg)" }} /> Back to inbox</button>
          ) : null}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 15, color: t.fg }}>{s.name || "(unknown sender)"}</div>
              {s.email && s.email !== s.name ? <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, marginTop: 1 }}>{s.email}</div> : null}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {selMeta ? (viewingArchived ? <Badge tone="neutral">Archived</Badge> : selMeta.answered ? <Badge tone="good">Answered</Badge> : <Badge tone="accent">Needs response</Badge>) : null}
              {selMeta && !selMeta.answered && !viewingArchived ? (
                !selMeta.claim ? (
                  <button style={{ ...actBtn, color: t.accent, borderColor: t.accent }} disabled={busy} onClick={() => claim(sel, false, selAccount)}><Icon name="lock" size={14} color={t.accent} /> I've got this one</button>
                ) : selMeta.claim.email === meEmail ? (
                  <>
                    <span style={{ ...pill(t.accentSoft, t.accent) }}><Icon name="lock" size={12} color={t.accent} /> You've got this</span>
                    <button style={{ ...actBtn }} disabled={busy} onClick={() => claim(sel, true, selAccount)}>Release</button>
                  </>
                ) : (
                  <span style={{ ...pill(t.badSoft, t.bad) }}><Icon name="lock" size={12} color={t.bad} /> {selMeta.claim.name}'s got this</span>
                )
              ) : null}
            </div>
          </div>
          <div style={{ fontFamily: OSWALD, fontSize: 20, fontWeight: 600, lineHeight: 1.25, color: t.fg, margin: "12px 0 8px" }}>{thread?.subject || selMeta?.subject || "(no subject)"}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontFamily: FIGTREE, fontSize: 12, color: t.faint }}>
            <span style={{ color: t.muted }}>To {selMeta?.mailbox || "this inbox"}</span>
            {stampSrc.rel ? <><span style={{ opacity: 0.5 }}>·</span><span style={{ color: t.accent, fontWeight: 800 }}>{stampSrc.rel}</span></> : null}
            {stampSrc.date ? <><span style={{ opacity: 0.5 }}>·</span><span style={{ color: t.fg2, fontWeight: 600 }}>{stampSrc.date}</span></> : null}
            {stampSrc.time ? <><span style={{ opacity: 0.5 }}>·</span><span>{stampSrc.time}</span></> : null}
            {ord ? <><span style={{ opacity: 0.5 }}>·</span><span style={{ color: t.muted }}>Order {ord}</span></> : null}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 14, paddingTop: 13, borderTop: "1px solid " + t.border, position: "relative" }}>
            <button style={actBtn} disabled={busy} onClick={() => triage(selMeta?.unread ? "read" : "unread", sel, selAccount)}><Icon name="mail" size={14} color={t.fg2} /> {selMeta?.unread ? "Mark read" : "Mark unread"}</button>
            <button style={actBtn} disabled={busy} onClick={() => triage("star", sel, selAccount)}><Icon name="star" size={14} color={t.fg2} /> Star</button>
            <div style={{ position: "relative" }}>
              <button style={{ ...actBtn, color: curTag ? curTag.color : t.fg2, borderColor: curTag ? curTag.color : t.borderStrong }} onClick={() => setTagMenu((v) => !v)}>
                <Icon name="tag" size={14} color={curTag ? curTag.color : t.fg2} /> {curTag ? curTag.label : "Tag"} <Icon name="chevron" size={11} color={curTag ? curTag.color : t.faint} style={{ transform: "rotate(90deg)" }} />
              </button>
              {tagMenu ? (
                <>
                  <div onClick={() => setTagMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
                  <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 41, background: t.surface, border: "1px solid " + t.borderStrong, borderRadius: 14, boxShadow: "0 20px 50px rgba(0,0,0,.45)", padding: 7, minWidth: 210 }}>
                    <button onClick={() => setThreadTag(sel, selAccount, "")} style={menuItem(t)}><span style={{ width: 10, height: 10, borderRadius: 5, background: t.muted, flex: "none" }} /> General (no tag){!selMeta?.tag ? <Icon name="check" size={14} color={t.accent} style={{ marginLeft: "auto" }} /> : null}</button>
                    <div style={{ height: 1, background: t.border, margin: "5px 4px" }} />
                    {TAGS.map((tg) => (
                      <button key={tg.k} onClick={() => setThreadTag(sel, selAccount, tg.k)} style={menuItem(t)}>
                        <span style={{ width: 10, height: 10, borderRadius: 5, background: tg.color, flex: "none" }} /> {tg.label}
                        {selMeta?.tag === tg.k ? <Icon name="check" size={14} color={t.accent} style={{ marginLeft: "auto" }} /> : null}
                      </button>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
            {viewingArchived
              ? <button style={{ ...actBtn, color: t.accent, borderColor: t.accent }} disabled={busy} onClick={() => triage("unarchive", sel, selAccount)}><Icon name="mail" size={14} color={t.accent} /> Move to Needs response</button>
              : <button style={actBtn} disabled={busy} onClick={() => triage("archive", sel, selAccount)}><Icon name="archive" size={14} color={t.fg2} /> Archive</button>}
            <button style={{ ...actBtn }} disabled={busy} onClick={notCustomer} title="Block this sender and archive — for marketing/automated mail that isn't a customer"><Icon name="x" size={14} color={t.fg2} /> Not a customer</button>
            <span style={{ marginLeft: "auto", fontFamily: FIGTREE, fontSize: 11, color: t.faint }}>Mark unread · Star · Archive apply to the whole conversation</span>
          </div>
        </div>

        <div style={sheet
          // mobile sheet: fixed overlay, messages scroll INSIDE the sheet
          ? { flex: "1 1 0", minHeight: 0, overflowY: "auto", padding: 18, display: "flex", flexDirection: "column", gap: 14, background: t.appBg }
          // desktop: natural height — every message shows in full and the PAGE scrolls
          : { padding: 18, display: "flex", flexDirection: "column", gap: 14, background: t.appBg }}>
          {sel && go && (() => {
            // "In the tracker" (SPEC §5.10): the app account behind this sender, beside the conversation.
            const first = thread?.messages?.find((m: any) => !m.fromMe);
            const email = parseSender(first?.from || selMeta?.sender || "").email;
            return email ? <InTheTracker email={email} go={go} threadKey={selAccount + ":" + sel} compact={!isWide || sheet} /> : null;
          })()}
          {threadLoading ? (
            <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>Loading conversation…</div>
          ) : !thread ? (
            <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>Couldn't load this conversation.</div>
          ) : thread.messages.map((m: any) => {
            const ms = parseSender(m.from);
            const mp = fmtParts(m.date);
            return (
              <div key={m.id} style={{ border: "1px solid " + t.border, borderRadius: 14, background: m.fromMe ? "linear-gradient(180deg,rgba(248,146,92,0.07)," + t.surface + ")" : t.surface, overflow: "hidden" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 15px", borderBottom: "1px solid " + t.border }}>
                  <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 12.5, color: m.fromMe ? t.accent : t.fg }}>{m.fromMe ? "You" : ms.name}</span>
                  <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ms.email}{m.via ? ` · via ${VIA_LABEL[m.via] || "a relay"}` : ""}</span>
                  <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, flexShrink: 0, textAlign: "right" }}>
                    {mp.rel ? <span style={{ color: t.accent, fontWeight: 800 }}>{mp.rel} </span> : null}
                    <span style={{ color: t.fg2, fontWeight: 700 }}>{mp.date}</span> · {mp.time}
                  </span>
                </div>
                <div style={{ padding: 14 }}><EmailBody text={m.text} html={m.html} /></div>
                {m.attachments && m.attachments.length ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10, padding: "0 14px 14px" }}>
                    {m.attachments.map((a: any) => <AttachmentChip key={a.attachmentId} account={selAccount} messageId={m.id} att={a} />)}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        {!viewingArchived ? (
          <div style={{ flex: "none", display: "grid", gap: 9, padding: "13px 18px", borderTop: "1px solid " + t.border, background: t.surface }}>
            {canSend ? (
              <>
                <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} onBlur={flushDraft} placeholder={`Reply from ${selMeta?.mailbox || "this inbox"}…`} rows={3}
                  style={{ width: "100%", boxSizing: "border-box", resize: "vertical", fontFamily: FIGTREE, fontSize: 13.5, padding: "11px 14px", borderRadius: 12, border: "1px solid " + t.borderStrong, background: t.surface2, color: t.fg, outline: "none", minHeight: 70 }} />
                {attach.length ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {attach.map((a, i) => (
                      <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: t.surface2, border: "1px solid " + t.border, borderRadius: 10, padding: "6px 10px", maxWidth: "100%" }}>
                        <Icon name="doc" size={13} color={t.purple} />
                        <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.fg2, maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.filename}</span>
                        <span style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint }}>{prettyBytes(a.size)}</span>
                        <button onClick={() => removeAttach(i)} aria-label="Remove attachment" style={{ border: "none", background: "transparent", color: t.faint, cursor: "pointer", padding: 0, display: "inline-flex" }}><Icon name="x" size={13} color={t.faint} /></button>
                      </span>
                    ))}
                  </div>
                ) : null}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint }}>
                    Sends from {selMeta?.mailbox}. Signature added automatically.
                    {selMeta?.draft ? <span style={{ color: t.purple, fontWeight: 700 }}> · Draft saved</span> : null}
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <label style={{ ...actBtn }}>
                      <Icon name="doc" size={14} color={t.fg2} /> Attach
                      <input type="file" multiple onChange={(e) => { addFiles(e.target.files); e.currentTarget.value = ""; }} style={{ display: "none" }} />
                    </label>
                    {selMeta?.draft ? <button onClick={discardDraft} style={{ ...actBtn }}>Discard draft</button> : null}
                    <Btn tone="accent" size="sm" icon="send" disabled={sending || (!replyText.trim() && !attach.length)} onClick={sendReplyNow}>{sending ? "Sending…" : "Send"}</Btn>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, display: "flex", alignItems: "center", gap: 6 }}>
                <Icon name="lock" size={13} color={t.faint} /> This is the boostblenz@gmail inbox — reply from Gmail directly. Dashboard send is on for support@ and dustin@.
              </div>
            )}
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <SectionTitle
        sub={view === "personal"
          ? "Your business dustin@boostblenz.com inbox — kept separate from customer service, visible only to you."
          : "Customer service — inquiries to support@boostblenz.com and boostblenz@gmail, merged. Automated, marketing, and our own mail are filtered out. A customer reply bumps a thread back to Needs response automatically."}
      >Help Desk</SectionTitle>

      {view === "customer" ? <CustomerServicePulse view={view} /> : null}

      {/* view toggle + Refresh on one row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 8 }}>
          {showPersonal ? (["customer", "personal"] as const).map((id) => {
            const on = view === id;
            return (
              <button key={id} onClick={() => setView(id)} style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 13, padding: "7px 16px", borderRadius: 999, cursor: "pointer", border: "1px solid " + (on ? t.accent : t.border), background: on ? t.accent : t.surface, color: on ? "#2a1608" : t.muted }}>
                {id === "customer" ? "Customer Service" : "Business"}
              </button>
            );
          }) : <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 13, padding: "7px 16px", borderRadius: 999, border: "1px solid " + t.accent, background: t.accent, color: "#2a1608" }}>Customer Service</span>}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {showPersonal ? <Btn variant="ghost" size="sm" icon="check" disabled={busy} onClick={setupGmail}>Set up Gmail</Btn> : null}
          <Btn variant="ghost" size="sm" icon="lock" onClick={() => setShowBlocked(true)}>Blocked senders</Btn>
          <Btn variant="ghost" size="sm" icon="refresh" onClick={() => (viewingArchived ? loadArchived(true) : loadList(true))} disabled={loading}>Refresh</Btn>
        </div>
      </div>

      {/* search + tag filter */}
      <div style={{ display: "grid", gap: 11 }}>
        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
          <span style={{ position: "absolute", left: 14, display: "inline-flex" }}><Icon name="search" size={17} color={t.faint} /></span>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, subject, order #, or any keyword in a message…"
            style={{ width: "100%", boxSizing: "border-box", fontFamily: FIGTREE, fontSize: 14, color: t.fg, background: t.surface, border: "1px solid " + t.borderStrong, borderRadius: 14, padding: "13px 42px 13px 42px", outline: "none" }} />
          {query ? <button onClick={() => setQuery("")} aria-label="Clear search" style={{ position: "absolute", right: 8, background: "transparent", border: "none", color: t.faint, cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 6 }}>×</button> : null}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 7, alignItems: "center" }}>
          <span style={{ fontFamily: FIGTREE, fontSize: 10.5, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: t.faint, marginRight: 2 }}>Filter</span>
          <button onClick={() => setTagFilter("all")} style={filterPill(t, tagFilter === "all", t.accent)}>All <span style={{ fontSize: 10.5, fontWeight: 800, opacity: 0.85 }}>{tagCounts.all}</span></button>
          {TAGS.map((tg) => (
            <button key={tg.k} onClick={() => setTagFilter(tg.k)} style={filterPill(t, tagFilter === tg.k, tg.color)}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: tg.color }} /> {tg.label} <span style={{ fontSize: 10.5, fontWeight: 800, opacity: 0.85 }}>{tagCounts[tg.k]}</span>
            </button>
          ))}
        </div>
      </div>

      <Card pad={0}>
        <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "0 8px", borderBottom: "1px solid " + t.border }}>
          <TabBtn id="active" label="Needs response" count={needs.length} tone={t.accent} />
          <TabBtn id="drafts" label="Drafts" count={draftThreads.length} tone={t.purple} />
          <TabBtn id="answered" label="Answered" count={answered.length} tone={t.good} />
          <TabBtn id="archived" label="Archived" count={archLoaded ? archived.length : null} tone={t.muted} />
          {searching ? <span style={{ marginLeft: "auto", padding: "0 12px", fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>{searchLoading ? "Searching…" : `${results.length} result${results.length === 1 ? "" : "s"} for “${query.trim()}”`}</span> : null}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "340px minmax(0,1fr)", alignItems: "start" }}>
          {/* ---- thread list. Desktop: sticky + own scroll, so it stays put while the
                  page scrolls the (full-height) conversation beside it. ---- */}
          <div style={isMobile
            ? { minWidth: 0 }
            : { borderRight: "1px solid " + t.border, position: "sticky", top: 80, alignSelf: "start", maxHeight: "calc(100vh - 96px)", overflowY: "auto", minWidth: 0 }}>
            {(searching ? searchLoading : loading) ? (
              <div style={{ padding: 20, fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>{searching ? "Searching…" : "Loading inbox…"}</div>
            ) : shown.length === 0 ? (
              <div style={{ padding: 20, fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>
                {searching ? `No messages match “${query.trim()}”.` : tagFilter !== "all" ? `Nothing tagged ${TAGMAP[tagFilter]?.label} here.` : (err || (tab === "active" ? "Nothing waiting — inbox zero. 🎉" : tab === "drafts" ? "No drafts. Start a reply and it saves here automatically — finish it whenever." : tab === "answered" ? "No answered threads in the last 30 days." : "No archived conversations yet. Archive a done thread and it lands here."))}
              </div>
            ) : shown.map((x) => {
              const s = parseSender(x.sender);
              const active = x.threadId === sel && x.account === selAccount;
              const tg = x.tag ? TAGMAP[x.tag] : null;
              return (
                <button key={x.account + ":" + x.threadId} onClick={() => openThread(x.threadId, x.account)} style={{ display: "block", width: "100%", textAlign: "left", padding: "13px 15px 13px 13px", border: "none", borderBottom: "1px solid " + t.border, borderLeft: "3px solid " + (active ? t.accent : x.unread ? "rgba(248,146,92,0.5)" : "transparent"), background: active ? t.surface2 : x.unread ? "rgba(248,146,92,0.07)" : "transparent", cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    {x.unread ? <span style={{ width: 8, height: 8, borderRadius: 4, background: t.accent, flexShrink: 0, boxShadow: "0 0 0 3px " + t.accentSoft }} /> : null}
                    <span style={{ fontFamily: FIGTREE, fontWeight: x.unread ? 800 : 700, fontSize: 13.5, color: t.fg, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</span>
                    {view === "customer" ? <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 10, padding: "1px 6px", borderRadius: 999, background: t.surface2, color: t.muted, flexShrink: 0 }}>{x.account === "support" ? "support@" : "gmail"}</span> : null}
                    <span style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint, flexShrink: 0 }}>{fmtWhen(x.receivedAt)}</span>
                  </div>
                  <div style={{ fontFamily: FIGTREE, fontWeight: x.unread ? 700 : 600, fontSize: 12.5, color: x.unread ? t.fg : t.fg2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 2 }}>{x.subject || "(no subject)"}</div>
                  <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{x.snippet}</div>
                  {tg ? <div style={{ marginTop: 7 }}><span style={{ ...pill(hexSoft(tg.color), tg.color), fontSize: 10 }}><span style={{ width: 6, height: 6, borderRadius: 3, background: tg.color }} /> {tg.label}</span></div> : null}
                  {x.draft ? <div style={{ marginTop: 6, fontFamily: FIGTREE, fontSize: 11.5, color: t.purple, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}><b>Draft:</b> {x.draft}</div> : null}
                  {x.claim ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6, fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, color: x.claim.email === meEmail ? t.accent : t.bad }}>
                      <Icon name="lock" size={12} color={x.claim.email === meEmail ? t.accent : t.bad} />
                      {x.claim.email === meEmail ? "You've got this" : `${x.claim.name}'s got this`}
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>

          {/* ---- reading pane: desktop column ---- */}
          {!isMobile ? (
            <div style={{ display: "flex", flexDirection: "column", background: t.surface, minWidth: 0, borderBottomRightRadius: t.cardRadius }}>
              {renderPane({ sheet: false })}
            </div>
          ) : null}
        </div>
      </Card>

      {/* ---- reading pane: mobile slide-up sheet ---- */}
      {isMobile ? (
        <div style={{ position: "fixed", inset: 0, zIndex: 60, background: t.surface, display: "flex", flexDirection: "column", transform: sheetOpen && sel ? "translateY(0)" : "translateY(100%)", transition: "transform .32s cubic-bezier(.22,.61,.36,1)", pointerEvents: sheetOpen && sel ? "auto" : "none" }}>
          {renderPane({ sheet: true })}
        </div>
      ) : null}

      {showBlocked ? <BlockedModal onClose={() => { setShowBlocked(false); loadList(true); }} /> : null}
    </div>
  );
}

// "6h 12m" / "24m" — how long a customer has been waiting.
function waitedFor(iso: string): { label: string; mins: number } {
  const d = new Date(iso).getTime();
  if (isNaN(d)) return { label: "", mins: 0 };
  const mins = Math.max(0, Math.round((Date.now() - d) / 60000));
  if (mins < 60) return { label: mins + "m", mins };
  const h = Math.floor(mins / 60);
  if (h < 24) return { label: `${h}h ${mins % 60}m`, mins };
  return { label: `${Math.floor(h / 24)}d ${h % 24}h`, mins };
}

// ---- Customer Service Pulse (radar) -----------------------------------------
// Read-only: who's waiting, how long (SLA color), Shopify context, and a deep-link
// straight into the Gmail thread. The actual reply happens in Gmail.
function CustomerServicePulse({ view }: { view: string }) {
  const t = useT();
  const [data, setData] = React.useState<any>(null);
  const load = React.useCallback(() => {
    fetch(`/api/helpdesk/pulse?view=${view}`).then((r) => (r.ok ? r.json() : null)).then((d) => d && setData(d)).catch(() => {});
  }, [view]);
  React.useEffect(() => { load(); const iv = setInterval(() => { if (!document.hidden) load(); }, 60000); return () => clearInterval(iv); }, [load]);

  const items = data?.items || [];
  const oldest = data?.oldestAt ? waitedFor(data.oldestAt) : null;
  const money = (n: number, cur: string) => new Intl.NumberFormat("en-US", { style: "currency", currency: cur || "USD", maximumFractionDigits: 0 }).format(n);
  const Tile = ({ lab, val, foot, alert }: any) => (
    <div style={{ background: "linear-gradient(160deg,#2a2154,#1f1943)", border: "1px solid " + t.border, borderRadius: 14, padding: "13px 15px" }}>
      <div style={{ fontFamily: OSWALD, textTransform: "uppercase", letterSpacing: ".1em", fontSize: 10, fontWeight: 600, color: t.muted }}>{lab}</div>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-.02em", marginTop: 4, color: alert ? t.accent : t.fg }}>{val}</div>
      {foot ? <div style={{ fontSize: 11, color: t.faint, marginTop: 2 }}>{foot}</div> : null}
    </div>
  );

  return (
    <Card pad={0} style={{ overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "13px 16px 0" }}>
        <div style={{ fontFamily: OSWALD, textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 600, fontSize: 15 }}>Customer Service Pulse</div>
        <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint }}>Reply in Gmail · this is your radar</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, padding: 16 }}>
        <Tile lab="Waiting on us" val={data ? items.length : "…"} foot="need a reply" alert />
        <Tile lab="Oldest wait" val={oldest ? oldest.label : "—"} foot={items[0] ? parseSender(items[0].sender).name : ""} />
      </div>
      <div style={{ borderTop: "1px solid " + t.border }}>
        {!data ? (
          <div style={{ padding: 18, fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 18, fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>Nobody waiting — inbox zero. 🎉</div>
        ) : items.map((x: any) => {
          const s = parseSender(x.sender);
          const w = waitedFor(x.receivedAt);
          const sev = w.mins >= 240 ? t.bad : w.mins >= 60 ? t.warn : t.good;
          const sevSoft = w.mins >= 240 ? t.badSoft : w.mins >= 60 ? t.warnSoft : t.goodSoft;
          const c = x.ctx;
          return (
            <div key={x.account + ":" + x.threadId} style={{ display: "flex", borderTop: "1px solid " + t.border }}>
              <div style={{ width: 4, background: sev, flex: "none" }} />
              <div style={{ padding: "13px 16px", flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 14, color: t.fg }}>{s.name}</span>
                  <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint }}>{s.email}</span>
                  <span style={{ marginLeft: "auto", fontFamily: FIGTREE, fontWeight: 800, fontSize: 11.5, padding: "2px 9px", borderRadius: 999, background: sevSoft, color: sev }}>waited {w.label}</span>
                </div>
                <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg2, margin: "6px 0 2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{x.subject || "(no subject)"}</div>
                <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{x.snippet}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10, alignItems: "center" }}>
                  {c && c.found ? (
                    <>
                      <span style={{ ...pill(t.purpleSoft, t.purple), fontSize: 11 }}>{c.orders} order{c.orders === 1 ? "" : "s"} · {money(c.spent, c.currency)} LTV</span>
                      {c.lastOrder ? <span style={{ ...pill(t.surface2, t.fg2), fontSize: 11 }}>{c.lastOrder} · {(c.fulfillment || "").toLowerCase()}</span> : null}
                    </>
                  ) : (
                    <span style={{ ...pill(t.surface2, t.faint), fontSize: 11 }}>No Shopify match</span>
                  )}
                  {x.tag ? <span style={{ ...pill(hexSoft(TAGMAP[x.tag]?.color || "#b9a9ff"), TAGMAP[x.tag]?.color || t.purple), fontSize: 11 }}>{TAGMAP[x.tag]?.label}</span> : null}
                  <a href={x.gmailUrl} target="_blank" rel="noopener noreferrer" style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 7, fontFamily: FIGTREE, fontWeight: 800, fontSize: 12, padding: "7px 13px", borderRadius: 10, background: t.accent, color: "#2a1608", textDecoration: "none" }}>
                    <Icon name="mail" size={14} color="#2a1608" /> Open in Gmail
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function prettyBytes(n: number): string {
  if (!n) return "";
  return n < 1024 * 1024 ? Math.max(1, Math.round(n / 1024)) + " KB" : (n / 1048576).toFixed(1) + " MB";
}

// Blocked-senders manager — view and un-block "not a customer" senders so a mistaken
// block is always recoverable.
function BlockedModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [rows, setRows] = React.useState<any[] | null>(null);
  const load = React.useCallback(() => {
    fetch("/api/helpdesk/blocked").then((r) => (r.ok ? r.json() : null)).then((d) => setRows(Array.isArray(d?.blocked) ? d.blocked : [])).catch(() => setRows([]));
  }, []);
  React.useEffect(() => { load(); }, [load]);
  async function unblock(email: string) {
    await fetch("/api/helpdesk/unblock", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ email }) }).catch(() => {});
    setRows((rs) => (rs || []).filter((r) => r.email !== email));
  }
  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 95, display: "grid", placeItems: "center", padding: 20 }}>
      <div style={{ width: 520, maxWidth: "100%", maxHeight: "80vh", display: "flex", flexDirection: "column", background: t.surface, border: "1px solid " + t.borderStrong, borderRadius: 16, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "15px 20px", borderBottom: "1px solid " + t.border }}>
          <Title size={15}>Blocked senders</Title>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", color: t.muted }}><Icon name="x" size={18} color={t.muted} /></button>
        </div>
        <div style={{ padding: "10px 20px", fontFamily: FIGTREE, fontSize: 12, color: t.muted, borderBottom: "1px solid " + t.border }}>Mail from these senders is hidden from the Help Desk. Unblock to let it back in.</div>
        <div style={{ overflowY: "auto", padding: 12 }}>
          {rows == null ? (
            <div style={{ padding: 14, fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>Loading…</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 14, fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>No blocked senders. Use “Not a customer” on a thread to add one.</div>
          ) : rows.map((r) => (
            <div key={r.email} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 8px", borderBottom: "1px solid " + t.border }}>
              <span style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.email}</span>
              <Btn variant="ghost" size="sm" onClick={() => unblock(r.email)}>Unblock</Btn>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// small style helpers
function pill(bg: string, color: string): React.CSSProperties {
  return { display: "inline-flex", alignItems: "center", gap: 5, fontFamily: FIGTREE, fontWeight: 800, fontSize: 11, padding: "3px 9px", borderRadius: 999, background: bg, color };
}
function filterPill(t: any, on: boolean, color: string): React.CSSProperties {
  return { fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 12px", borderRadius: 999, cursor: "pointer", background: on ? hexSoft(color) : t.surface2, border: "1px solid " + (on ? color : t.border), color: on ? color : t.muted };
}
function menuItem(t: any): React.CSSProperties {
  return { display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", background: "transparent", border: "none", borderRadius: 9, padding: "9px 11px", cursor: "pointer", fontFamily: FIGTREE, fontWeight: 700, fontSize: 13, color: t.fg2 };
}
// soft translucent version of a hex accent (for tag chip backgrounds)
function hexSoft(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return "rgba(185,169,255,0.16)";
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},0.16)`;
}

// ============================================================ KNOWLEDGE BASE
export function KnowledgeBaseView() {
  const t = useT();
  const seed = (BB_HELP && BB_HELP.KB) || [];
  const [cards, setCards] = React.useState(seed);
  const [guards, setGuards] = React.useState({ noMakeUp: true, cite: true, injection: true, dataWall: true, escalate: true });
  const [edit, setEdit] = React.useState(null); // {idx, card} or null

  function save(card, idx) {
    if (idx == null || idx < 0) setCards([...cards, { ...card, id: 'kb' + Date.now() }]);
    else setCards(cards.map((c, i) => (i === idx ? { ...c, ...card } : c)));
    setEdit(null);
  }
  function del(idx) { if (confirm('Delete this help card? The AI will no longer use it.')) setCards(cards.filter((_, i) => i !== idx)); }

  const gLabel = {
    noMakeUp: ['Never invent answers', 'AI may only combine facts from the cards above. No match → it says it doesn’t know and offers a human.'],
    cite: ['Cite the source card', 'Each answer is grounded to a specific card (shown to your team, hidden from the customer).'],
    injection: ['Prompt-injection shield', 'Ignores any attempt to change its instructions, reveal its prompt, or run commands.'],
    dataWall: ['Customer-data firewall', 'No access to orders, PII, systems, or financials. Runs in an isolated service + database.'],
    escalate: ['Auto-escalate health/medical', 'Medication, pregnancy, symptoms, refunds → routed to a human, never answered by AI.'],
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <SectionTitle sub="These cards are the only facts the AI may use. Edit one and every answer updates." right={<Btn tone="accent" icon="check" onClick={() => setEdit({ idx: -1, card: { q: '', short: '', topic: 'diet', source: '', escalate: false } })}>Add help card</Btn>}>Knowledge Base</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px', gap: 16 }}>
        <div style={{ display: 'grid', gap: 12 }}>
          {cards.map((k, i) => (
            <Card key={k.id || i} pad={16}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 14, color: t.fg }}>{k.q}</div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <Btn variant="ghost" size="sm" onClick={() => setEdit({ idx: i, card: k })}>Edit</Btn>
                  <Btn variant="ghost" size="sm" tone="bad" onClick={() => del(i)}>Delete</Btn>
                </div>
              </div>
              <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted, lineHeight: 1.55, margin: '8px 0 12px' }}>{k.short}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Badge tone="purple">{k.topic}</Badge>
                {k.escalate ? <Badge tone="bad">Always escalate</Badge> : <Badge tone="good">AI-answerable</Badge>}
                {k.source && <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint }}>Source: {k.source}</span>}
              </div>
            </Card>
          ))}
        </div>
        <div style={{ display: 'grid', gap: 14, alignContent: 'start' }}>
          <Card pad={18}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4 }}><Icon name="lock" size={17} color={t.fg} /><Title size={14}>AI guardrails</Title></div>
            <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, marginBottom: 4 }}>Applied to every customer answer, server-side.</div>
            {Object.keys(gLabel).map((key, i) => (
              <div key={key} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 0', borderTop: '1px solid ' + t.border }}>
                <Toggle on={guards[key]} onClick={() => setGuards({ ...guards, [key]: !guards[key] })} />
                <div><div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13, color: t.fg }}>{gLabel[key][0]}</div><div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, lineHeight: 1.5 }}>{gLabel[key][1]}</div></div>
              </div>
            ))}
          </Card>
          <Card pad={18}>
            <Title size={14} style={{ marginBottom: 10 }}>Escalation triggers</Title>
            <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, marginBottom: 10 }}>Phrases that force a human hand-off:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {['medication', 'pregnant', 'nursing', 'doctor', 'refund', 'return', 'order status', 'shipping', 'allergic', 'diabetic'].map((w) => <Badge key={w} tone="neutral">{w}</Badge>)}
            </div>
          </Card>
        </div>
      </div>
      {edit && <KbModal state={edit} onSave={save} onClose={() => setEdit(null)} />}
    </div>
  );
}

function KbModal({ state, onSave, onClose }) {
  const t = useT();
  const [c, setC] = React.useState({ ...state.card });
  const field: any = { fontFamily: FIGTREE, fontSize: 13.5, padding: '10px 12px', borderRadius: t.cardRadius || 6, border: '1px solid ' + t.borderStrong, background: t.surface, color: t.fg, outline: 'none', width: '100%', boxSizing: 'border-box' };
  const lbl: any = { fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: t.faint, display: 'block', marginBottom: 6 };
  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 90, display: 'grid', placeItems: 'center', padding: 20 }}>
      <div style={{ width: 540, maxWidth: '100%', background: t.surface, border: '1px solid ' + t.borderStrong, borderRadius: t.cardRadius || 8, boxShadow: '0 20px 60px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px 20px', borderBottom: '1px solid ' + t.border }}>
          <Title size={15}>{state.idx >= 0 ? 'Edit help card' : 'Add help card'}</Title>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: t.muted }}><Icon name="x" size={18} color={t.muted} /></button>
        </div>
        <div style={{ padding: 20, display: 'grid', gap: 14 }}>
          <div><label style={lbl}>Customer question</label><input style={field} value={c.q} onChange={(e) => setC({ ...c, q: e.target.value })} /></div>
          <div><label style={lbl}>Answer (the only facts the AI may use)</label><textarea rows={4} style={{ ...field, resize: 'vertical' }} value={c.short} onChange={(e) => setC({ ...c, short: e.target.value })} /></div>
          <div style={{ display: 'flex', gap: 14 }}>
            <div style={{ flex: 1 }}><label style={lbl}>Topic</label><input style={field} value={c.topic || ''} onChange={(e) => setC({ ...c, topic: e.target.value })} /></div>
            <div style={{ flex: 1 }}><label style={lbl}>Source</label><input style={field} value={c.source || ''} onChange={(e) => setC({ ...c, source: e.target.value })} /></div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Toggle on={!!c.escalate} onClick={() => setC({ ...c, escalate: !c.escalate })} />
            <div><div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13, color: t.fg }}>Always escalate to a human</div><div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted }}>For health/medical topics the AI must never answer.</div></div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '1px solid ' + t.border }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn tone="accent" icon="check" onClick={() => onSave(c, state.idx)}>Save card</Btn>
        </div>
      </div>
    </div>
  );
}
